// Rutgers University-New Brunswick: public college page (#2652, round 2),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Departure: Rutgers' Office of Institutional Research and Academic
//    Planning posts New Brunswick Common Data Sets only through "New
//    Brunswick CDS_2023-2024_final_V1.pdf" (Fall 2023); no 2024-2025 or
//    2025-2026 edition is posted. As on the Camden and Newark pages, the
//    first-year counts come from the U.S. Department of Education's IPEDS
//    Admissions survey for Fall 2024, first-time degree-seeking students
//    (the ADM2024 complete data file, unit 186380: APPLCN 68,614, ADMSSN
//    39,899, ENRLT 8,191; College Navigator shows the same 68,614 applicants,
//    58% admitted and 21% enrolled). IPEDS gives no residency split and no
//    early action counts, so this page has neither.
//  - admissions.rutgers.edu: Application Dates and Deadlines for New
//    Brunswick (the Fall/September 2027 table), Required Credentials for
//    First-Year Applicants (the New Brunswick test policy, Mason Gross talent
//    assessment, EOF) and How We Make Decisions (New Brunswick notification
//    guarantees and high school course requirements by school).
//  - Rutgers Scarlet Hub, Rutgers Students Cost of Attendance: the 2026-2027
//    In-State and Out-of-State New Brunswick Full-Time Undergraduate budgets
//    (scarlethub.rutgers.edu PDFs linked from that page), School of Arts and
//    Sciences, on campus. Each column sums exactly to its total. Other
//    schools' tuition and fees differ and are named in the note.
//  - College Scorecard API, school id 186380 (net price by family income).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=186380#admsns',
        read: '2026-10-01',
    },
    oirap: {
        label: 'Rutgers Office of Institutional Research and Academic Planning, Common Data Set',
        url: 'https://oirap.rutgers.edu/ReportingCommonDataSet.html',
        read: '2026-10-01',
    },
    dates: {
        label: 'Rutgers Undergraduate Admissions, Application Dates and Deadlines, New Brunswick',
        url: 'https://admissions.rutgers.edu/apply/dates-deadlines/new-brunswick',
        read: '2026-10-01',
    },
    firstyear: {
        label: 'Rutgers Undergraduate Admissions, Required Credentials for First-Year Applicants',
        url: 'https://admissions.rutgers.edu/apply/first-year-applicants',
        read: '2026-10-01',
    },
    decisions: {
        label: 'Rutgers Undergraduate Admissions, How We Make Decisions',
        url: 'https://admissions.rutgers.edu/apply/how-we-make-decisions',
        read: '2026-10-01',
    },
    coa: {
        label: 'Rutgers Scarlet Hub, Rutgers Students Cost of Attendance, 2026-2027',
        url: 'https://scarlethub.rutgers.edu/financial-services/cost-of-attendance/rutgers-students-cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?186380-Rutgers-University-New-Brunswick',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'rutgers-university-new-brunswick',
    kbId: 'rutgers_university_new_brunswick',
    name: 'Rutgers University-New Brunswick',
    shortName: 'Rutgers-New Brunswick',
    location: 'New Brunswick, NJ',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Rutgers identity guidelines); decoration only.
    branding: {
        primary: '#CC0033',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/rutgers_university_new_brunswick_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 68614,
        admitted: 39899,
        enrolled: 8191,
        source: 'ipeds',
        where: 'first-time, degree-seeking first-year students',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Rutgers-New Brunswick has not posted a Common Data Set since 2023-2024, so these counts come from the Department of Education\'s IPEDS survey for Fall 2024, which gives one first-year rate for the whole campus and none by school or major.',
        source: 'oirap',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Rutgers-New Brunswick has a nonbinding early action plan and no early decision plan: its first-year dates list only early action and regular decision. IPEDS gives no early action counts, so this page shows no early action acceptance rate.',
        source: 'dates',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to a school within Rutgers-New Brunswick. Every school asks for 16 academic high school courses. The School of Arts and Sciences and Rutgers Business School ask for four years of English, two years of one foreign language, three years of math (Algebra I, Geometry and Algebra II), two years of science and five other academic courses. Other schools differ:',
        rows: [
            { school: 'School of Engineering', requirement: 'Four years of English, four years of math (Algebra I, Geometry, Algebra II and Precalculus), a year each of chemistry and physics, and six other academic courses (computer programming recommended). No foreign language is required. Calculus is recommended in senior year if you have finished Precalculus.' },
            { school: 'School of Environmental and Biological Sciences', requirement: 'Four years of English, three years of math (Algebra I, Geometry and Algebra II), two years of science and seven other academic courses. No foreign language is required.' },
            { school: 'Ernest Mario School of Pharmacy', requirement: 'Four years of English, two years of one foreign language, three years of math (Algebra I, Geometry and Algebra II), a year each of biology and chemistry, and five other academic courses (physics recommended).' },
            { school: 'School of Nursing', requirement: 'Four years of English, three years of math (Algebra I, Geometry and Algebra II), a year each of biology and chemistry, and seven other academic courses. No foreign language is required.' },
            { school: 'Mason Gross School of the Arts', requirement: 'A portfolio, audition or interview is required of every applicant. Rutgers emails a supplemental application five business days after you apply. Mason Gross applicants hear by April 1, not on the early action or regular decision dates.', source: 'firstyear' },
            { school: 'Educational Opportunity Fund (EOF)', requirement: 'Open to eligible New Jersey residents. Select the EOF option on the Rutgers Application or Common App, then complete the EOF application.', source: 'firstyear' },
        ],
        source: 'decisions',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'December 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By January 31', binding: false },
            { plan: 'Regular Decision', deadline: 'December 1', decision: 'By February 28', binding: false },
        ],
        note: 'Rutgers calls these due dates: after them you may still apply under rolling admission, on a space-available basis, to any school still open on the application, and you hear after early action and regular decision applicants. To be considered for honors and merit-based scholarships, apply by December 1. Early action applicants deferred to regular decision get a final decision by the end of February. Decisions are posted in the Rutgers Application Portal, and the reply date is May 1.',
        source: 'dates',
        binding: 'Early action is not binding and does not restrict you from applying to other colleges.',
        bindingSource: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Rutgers-New Brunswick is test-optional: if you send SAT or ACT scores, they are an additional credential in your review, and if you do not, you still get full consideration. Rutgers reviews every score sent, so do not have scores sent if you do not want them considered. Scores must come from the testing agency. Rutgers says it maintains its test-optional and test-blind policies "through 2027".',
        source: 'firstyear',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstyear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [15381, 36831] },
            { item: 'Fees', amounts: [4008, 4008] },
            { item: 'Food and housing (on campus)', amounts: [21946, 21946] },
            { item: 'Books', amounts: [1504, 1504] },
            { item: 'Travel', amounts: [1026, 1598] },
            { item: 'Loan fees', amounts: [110, 110] },
            { item: 'Miscellaneous', amounts: [3704, 3704] },
        ],
        totals: [47679, 69701],
        note: 'Rutgers\' estimated budget for a full-time undergraduate in the School of Arts and Sciences living on campus. Tuition and fees differ by school: in the School of Engineering, tuition is $17,609 in-state and $43,036 out-of-state, for totals of $49,994 and $75,993; the business school, Environmental and Biological Sciences, Mason Gross, Nursing and the Bloustein School also differ. Rutgers calls these figures estimates; final tuition and fees are set each July.',
        source: 'coa',
        netPrice: {
            average: 24897,
            byIncome: [
                { income: '$0 to $30,000', amount: 15090 },
                { income: '$30,001 to $48,000', amount: 17496 },
                { income: '$48,001 to $75,000', amount: 18838 },
                { income: '$75,001 to $110,000', amount: 24156 },
                { income: 'Over $110,000', amount: 34232 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Rutgers\' aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
